/**
 * Surgical optimistic edits for the paginated list caches (`{ data, meta }`).
 *
 * WHY: measurement showed the API is fast (24–54 ms locally), so the felt lag
 * after a save is the SECOND network round-trip — a mutation resolves, then the
 * UI waits for the list *refetch* before the new/edited row appears. On
 * production (added network latency to the DB region) that doubles the wait.
 *
 * These helpers write the row straight into the cached list so it appears in
 * <100 ms, independent of the background refetch. They are:
 *  - **surgical**: only the matching list cache is touched; if it isn't in the
 *    cache yet, they no-op and the normal refetch fills it.
 *  - **envelope-aware**: they keep `meta.total` in sync.
 *  - **reversible**: `snapshotList` / `restoreSnapshot` capture the exact prior
 *    value so `onError` can roll back cleanly.
 *
 * The row shape stored in the cache is the RAW API entity (the list hooks map
 * to the UI type at read time), so callers pass the raw API object — typically
 * the mutation's own response, or an optimistic stand-in for onMutate.
 */

import type { QueryClient, QueryKey } from "@tanstack/react-query";

interface ListEnvelope<T> {
  data: T[];
  meta?: { page?: number; limit?: number; total?: number };
}

type HasId = { id: string };

function bumpTotal(meta: ListEnvelope<unknown>["meta"], delta: number, fallbackLen: number) {
  if (!meta) return meta;
  const current = typeof meta.total === "number" ? meta.total : fallbackLen;
  return { ...meta, total: Math.max(0, current + delta) };
}

/** Prepend a freshly-created row to the top of a list cache (newest-first). */
export function prependToList<T>(qc: QueryClient, key: QueryKey, item: T): void {
  qc.setQueryData<ListEnvelope<T>>(key, (old) =>
    old
      ? { ...old, data: [item, ...old.data], meta: bumpTotal(old.meta, +1, old.data.length) }
      : old,
  );
}

/** Replace an existing row (matched by id) with an updated version, in place. */
export function replaceInList<T extends HasId>(qc: QueryClient, key: QueryKey, item: T): void {
  qc.setQueryData<ListEnvelope<T>>(key, (old) =>
    old ? { ...old, data: old.data.map((row) => (row.id === item.id ? item : row)) } : old,
  );
}

/** Merge a partial patch into an existing row (matched by id). */
export function patchInList<T extends HasId>(
  qc: QueryClient,
  key: QueryKey,
  id: string,
  patch: Partial<T>,
): void {
  qc.setQueryData<ListEnvelope<T>>(key, (old) =>
    old ? { ...old, data: old.data.map((row) => (row.id === id ? { ...row, ...patch } : row)) } : old,
  );
}

/** Remove a row (matched by id) — used for archive so it disappears instantly. */
export function removeFromList<T extends HasId>(qc: QueryClient, key: QueryKey, id: string): void {
  qc.setQueryData<ListEnvelope<T>>(key, (old) => {
    if (!old) return old;
    const next = old.data.filter((row) => row.id !== id);
    if (next.length === old.data.length) return old;
    return { ...old, data: next, meta: bumpTotal(old.meta, -1, old.data.length) };
  });
}

/**
 * Find a row by id across one or more cached list envelopes.
 *
 * Used to SEED a detail query (`initialData`) from the list the user just
 * clicked: the detail page paints instantly with the row's scalars while the
 * real fetch (relations, rollups) completes in the background. Callers must
 * pair this with `initialDataUpdatedAt: () => 0` so the seeded data is
 * considered stale and the background fetch ALWAYS runs — list rows lack the
 * relations the detail view needs.
 */
export function findInLists<T extends HasId>(
  qc: QueryClient,
  keys: QueryKey[],
  id: string | undefined,
): T | undefined {
  if (!id) return undefined;
  for (const key of keys) {
    const row = qc.getQueryData<ListEnvelope<T>>(key)?.data.find((item) => item.id === id);
    if (row) return row;
  }
  return undefined;
}

/** Snapshot the current value of one or more list caches for rollback. */
export function snapshotList(qc: QueryClient, keys: QueryKey[]): Array<[QueryKey, unknown]> {
  return keys.map((key) => [key, qc.getQueryData(key)] as [QueryKey, unknown]);
}

/** Restore snapshots captured by `snapshotList` (call in `onError`). */
export function restoreSnapshot(qc: QueryClient, snapshot: Array<[QueryKey, unknown]>): void {
  for (const [key, value] of snapshot) qc.setQueryData(key, value);
}
